import {
	useEffect,
	useMemo,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { TextareaField } from '#app/components/form/textarea-field.tsx'
import { YesNoField } from '#app/components/form/yes-no-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import {
	SUPPLIER_COPY,
	SUPPLIER_RISK_LEVEL_OPTIONS,
} from '#app/routes/mocra+/_form+/components/suppliers/constants.ts'
import {
	createSupplierDraft,
	sanitizeSupplierDraft,
	type SupplierDraft,
} from '#app/routes/mocra+/_form+/components/suppliers/types.ts'

type Mode = 'add' | 'edit'

type DraftErrors = Partial<
	Pick<SupplierDraft, 'name' | 'materialScope' | 'contactEmail'>
>

type Props = {
	readonly mode: Mode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: SupplierDraft) => void
	readonly initialValue?: SupplierDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function SupplierOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<SupplierDraft>(
		initialValue ? { ...initialValue } : createSupplierDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createSupplierDraft())
			setErrors({})
		} else if (!open) {
			onClosed?.()
		}
	}, [initialValue, onClosed, open])

	const descriptionCopy = SUPPLIER_COPY.description
	const titleCopy =
		mode === 'add' ? SUPPLIER_COPY.title.add : SUPPLIER_COPY.title.edit
	const submitCopy =
		mode === 'add' ? SUPPLIER_COPY.submit.add : SUPPLIER_COPY.submit.edit

	const riskLevelOptions = useMemo(() => SUPPLIER_RISK_LEVEL_OPTIONS, [])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeSupplierDraft(draft)
		const nextErrors: DraftErrors = {}

		if (!sanitized.name) {
			nextErrors.name = 'Enter the supplier or manufacturer name.'
		}
		if (!sanitized.materialScope) {
			nextErrors.materialScope =
				'Describe the material or service they provide.'
		}
		if (
			sanitized.contactEmail &&
			!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized.contactEmail)
		) {
			nextErrors.contactEmail = 'Enter a valid contact email.'
		}

		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}

		onSubmit(sanitized)
		onOpenChange(false)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={descriptionCopy}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="supplier-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="supplier-overlay-form" onSubmit={handleSubmit}>
				<Field
					labelProps={{ children: SUPPLIER_COPY.fields.nameLabel }}
					inputProps={{
						value: draft.name,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, name: event.target.value })),
						type: 'text',
					}}
					errors={errors.name ? [errors.name] : undefined}
				/>
				<TextareaField
					labelProps={{ children: SUPPLIER_COPY.fields.materialScopeLabel }}
					textareaProps={{
						value: draft.materialScope,
						onChange: (event: ChangeEvent<HTMLTextAreaElement>) =>
							setDraft((prev) => ({
								...prev,
								materialScope: event.target.value,
							})),
						rows: 3,
					}}
					descriptionProps={{
						children: SUPPLIER_COPY.fields.materialScopeDescription,
					}}
					errors={errors.materialScope ? [errors.materialScope] : undefined}
				/>
				<SelectField
					labelProps={{ children: SUPPLIER_COPY.fields.riskLevelLabel }}
					selectProps={{
						value: draft.riskLevel,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								riskLevel: event.target.value as SupplierDraft['riskLevel'],
							})),
					}}
					options={riskLevelOptions}
					descriptionProps={{
						children: SUPPLIER_COPY.fields.riskLevelDescription,
					}}
				/>
				<YesNoField
					labelProps={{ children: SUPPLIER_COPY.fields.qualityAgreementLabel }}
					yesProps={{
						checked: draft.qualityAgreementSigned,
						onChange: () =>
							setDraft((prev) => ({
								...prev,
								qualityAgreementSigned: true,
							})),
						name: 'qualityAgreementSigned',
					}}
					noProps={{
						checked: !draft.qualityAgreementSigned,
						onChange: () =>
							setDraft((prev) => ({
								...prev,
								qualityAgreementSigned: false,
							})),
						name: 'qualityAgreementSigned',
					}}
				/>
				<Field
					labelProps={{ children: SUPPLIER_COPY.fields.contactEmailLabel }}
					inputProps={{
						value: draft.contactEmail,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({
								...prev,
								contactEmail: event.target.value,
							})),
						type: 'email',
					}}
					errors={errors.contactEmail ? [errors.contactEmail] : undefined}
				/>
			</form>
		</ResponsiveOverlay>
	)
}
